// src/features/chat/hooks/useHisActions.js
/*
 * Artifact action machinery for the HIS timeline.
 *
 * Ports the inbox runAction semantics: per-action busy keys, a shared lock
 * key per approval (confirm/decline can't race), a terminal-result overlay
 * so resolution renders before the Firestore snapshot catches up, and a
 * pruning effect that clears overlays once docs leave pending or disappear.
 */

import { useEffect, useRef, useState } from 'react';
import { terminalApprovalResult } from '../models/artifactLifecycleModel';
import { actionErrorText } from '../templates/terminalTraceTemplates';

/**
 * @param {Object} params
 * @param {Array} params.approvalRequests - Live approval docs (for pruning).
 * @param {Function} params.onUndoReceipt - (receiptId) => Promise<result>
 * @param {Function} params.onApproveRequest - (approvalId) => Promise<result>
 * @param {Function} params.onRejectRequest - (approvalId) => Promise<result>
 * @param {boolean} params.disabled
 * @returns {{
 *   busyKeys: Set<string>,
 *   actionNotice: {key: string, text: string}|null,
 *   terminalResultsById: Object,
 *   runUndo: (receiptId: string) => Promise<void>,
 *   runDecision: (approvalId: string, decision: 'confirm'|'decline') => Promise<void>,
 * }}
 */
export function useHisActions({
  approvalRequests = [],
  onUndoReceipt,
  onApproveRequest,
  onRejectRequest,
  disabled = false,
}) {
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const busyKeysRef = useRef(new Set());
  const [actionNotice, setActionNotice] = useState(null);
  const [terminalResultsById, setTerminalResultsById] = useState(() => ({}));

  // Clear overlays once Firestore observes the terminal state (or the doc
  // disappears) — local state must never outlive server truth.
  useEffect(() => {
    setTerminalResultsById((current) => {
      let changed = false;
      const next = { ...current };
      const liveApprovalIds = new Set();
      approvalRequests.forEach((approval) => {
        liveApprovalIds.add(approval.id);
        if ((approval.status || 'pending') !== 'pending' && next[approval.id]) {
          delete next[approval.id];
          changed = true;
        }
      });
      Object.keys(next).forEach((approvalId) => {
        if (!liveApprovalIds.has(approvalId)) {
          delete next[approvalId];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [approvalRequests]);

  const runAction = async (actionKey, action, lockKey = actionKey, options = {}) => {
    if (!action || disabled) return;
    if (busyKeysRef.current.has(lockKey)) return;
    busyKeysRef.current.add(lockKey);
    setBusyKeys((current) => {
      const next = new Set(current);
      next.add(actionKey);
      return next;
    });
    setActionNotice(null);
    try {
      const result = await action();
      const terminalResult = terminalApprovalResult(result);
      if (options.approvalId && terminalResult) {
        setTerminalResultsById((current) => ({
          ...current,
          [options.approvalId]: terminalResult,
        }));
      } else if (result?.success === false) {
        setActionNotice({ key: actionKey, text: actionErrorText(result) });
      }
    } catch (actionErr) {
      console.error('Zero-effort artifact action failed:', actionErr);
      setActionNotice({ key: actionKey, text: actionErrorText(actionErr) });
    } finally {
      setBusyKeys((current) => {
        if (!current.has(actionKey)) return current;
        const next = new Set(current);
        next.delete(actionKey);
        return next;
      });
      busyKeysRef.current.delete(lockKey);
    }
  };

  const runUndo = (receiptId) => runAction(
    `receipt:${receiptId}`,
    onUndoReceipt ? () => onUndoReceipt(receiptId) : null
  );

  const runDecision = (approvalId, decision) => {
    const isConfirm = decision === 'confirm';
    const handler = isConfirm ? onApproveRequest : onRejectRequest;
    return runAction(
      `approval-${isConfirm ? 'approve' : 'reject'}:${approvalId}`,
      handler ? () => handler(approvalId) : null,
      `approval:${approvalId}`,
      { approvalId }
    );
  };

  return {
    busyKeys,
    actionNotice,
    terminalResultsById,
    runUndo,
    runDecision,
  };
}
